/**
 * /admin/settings (BUILD_SPEC.md §6.7, §5.5).
 *
 * One row per settings key with a typed input — number for the
 * intervals, string for office names, multi-select chips for the
 * weekend days. Each row saves independently so a typo in one field
 * doesn't block updates to the others; we re-fetch after every save
 * so the cached value matches the server's view (even if the server
 * normalizes the input).
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { getSettings, updateSetting, type SettingKey, type SettingValue } from "@/api/settings";

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

type WeekDay = (typeof WEEKDAYS)[number];

const NUMBER_KEYS: SettingKey[] = [
  "archive_working_days",
  "polling_interval_min",
  "polling_interval_max",
  "scraper_concurrency",
];
const STRING_KEYS: SettingKey[] = ["office_name_ar", "office_name_en"];
// §16.1 sound prefs. Master + three per-keyword switches; the volume
// slider gets its own row for the 0–100 input affordance.
const BOOL_SOUND_KEYS: SettingKey[] = [
  "sound_master_enabled",
  "sound_approval_enabled",
  "sound_assignment_enabled",
  "sound_rejection_enabled",
];

export function SettingsAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const query = useQuery({ queryKey: ["settings"], queryFn: getSettings });

  // Tagged-union variant payload because React Query's useMutation
  // can't carry a generic-mutation-function — `variables` is a single
  // type, so we widen here and let `updateSetting` re-narrow.
  type Variant = {
    [K in SettingKey]: { key: K; value: SettingValue[K] };
  }[SettingKey];
  const updateMutation = useMutation<void, Error, Variant>({
    mutationFn: ({ key, value }) =>
      // The cast is sound: Variant guarantees `value` matches `SettingValue[key]`.
      updateSetting(key as SettingKey, value as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });

  // Per-key busy flag — the mutation's `variables.key` tells which row
  // is currently saving. Used by SaveButton to show a spinner only on
  // the row that triggered the save.
  const busyKey: SettingKey | false =
    updateMutation.isPending && updateMutation.variables ? updateMutation.variables.key : false;

  return (
    <div className="space-y-3">
      <h2 className="text-xl font-bold text-fg dark:text-fg-dark">{t("admin.settings")}</h2>

      {query.isLoading ? (
        <p className="text-sm text-muted dark:text-muted-dark">{t("common.loading")}</p>
      ) : (
        <div className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark divide-y divide-hairline dark:divide-hairline-dark">
          {NUMBER_KEYS.map((k) => (
            <NumberRow
              key={k}
              settingKey={k}
              initial={query.data?.[k] as number | undefined}
              save={(value) => updateMutation.mutateAsync({ key: k, value } as Variant)}
              busyKey={busyKey}
            />
          ))}
          <WeekendRow
            initial={query.data?.weekend_days as WeekDay[] | undefined}
            save={(value) =>
              updateMutation.mutateAsync({ key: "weekend_days", value } as Variant)
            }
            busy={busyKey === "weekend_days"}
          />
          {STRING_KEYS.map((k) => (
            <StringRow
              key={k}
              settingKey={k}
              initial={query.data?.[k] as string | undefined}
              save={(value) => updateMutation.mutateAsync({ key: k, value } as Variant)}
              busyKey={busyKey}
            />
          ))}
        </div>
      )}

      <h3 className="pt-4 text-lg font-semibold text-fg dark:text-fg-dark">
        {t("sound.title")}
      </h3>
      {!query.isLoading && (
        <div className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark divide-y divide-hairline dark:divide-hairline-dark">
          {BOOL_SOUND_KEYS.map((k) => (
            <BoolRow
              key={k}
              settingKey={k}
              initial={query.data?.[k] as boolean | undefined}
              save={(value) => updateMutation.mutateAsync({ key: k, value } as Variant)}
              busyKey={busyKey}
            />
          ))}
          <VolumeRow
            initial={query.data?.sound_volume as number | undefined}
            save={(value) =>
              updateMutation.mutateAsync({ key: "sound_volume", value } as Variant)
            }
            busy={busyKey === "sound_volume"}
          />
        </div>
      )}
    </div>
  );
}

interface NumberRowProps {
  settingKey: SettingKey;
  initial: number | undefined;
  save: (value: number) => Promise<void>;
  busyKey: SettingKey | false;
}

function NumberRow({ settingKey, initial, save, busyKey }: NumberRowProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>("");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (initial !== undefined) setValue(String(initial));
  }, [initial]);

  const dirty = value !== "" && initial !== undefined && Number(value) !== initial;
  const busy = busyKey === settingKey;

  return (
    <div className="flex items-center justify-between gap-3 p-4">
      <label className="flex-1">
        <span className="block text-sm text-fg dark:text-fg-dark mb-1">
          {t(`settings.${settingKey}`)}
        </span>
        <input
          type="number"
          inputMode="numeric"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-32 rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-3 py-1.5 text-sm text-fg dark:text-fg-dark"
        />
      </label>
      <SaveButton
        onClick={async () => {
          await save(Number(value));
          setSavedAt(Date.now());
        }}
        disabled={!dirty || busy}
        busy={busy}
        savedAt={savedAt}
      />
    </div>
  );
}

interface StringRowProps {
  settingKey: SettingKey;
  initial: string | undefined;
  save: (value: string) => Promise<void>;
  busyKey: SettingKey | false;
}

function StringRow({ settingKey, initial, save, busyKey }: StringRowProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>("");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (initial !== undefined) setValue(initial);
  }, [initial]);

  const dirty = initial !== undefined && value !== initial;
  const busy = busyKey === settingKey;

  return (
    <div className="flex items-center justify-between gap-3 p-4">
      <label className="flex-1">
        <span className="block text-sm text-fg dark:text-fg-dark mb-1">
          {t(`settings.${settingKey}`)}
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="rtl-isolate w-full rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-3 py-1.5 text-sm text-fg dark:text-fg-dark"
        />
      </label>
      <SaveButton
        onClick={async () => {
          await save(value);
          setSavedAt(Date.now());
        }}
        disabled={!dirty || busy}
        busy={busy}
        savedAt={savedAt}
      />
    </div>
  );
}

interface WeekendRowProps {
  initial: WeekDay[] | undefined;
  save: (value: WeekDay[]) => Promise<void>;
  busy: boolean;
}

function WeekendRow({ initial, save, busy }: WeekendRowProps) {
  const { t } = useTranslation();
  const [days, setDays] = useState<WeekDay[]>([]);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (initial) setDays(initial);
  }, [initial]);

  const dirty = useMemo(() => {
    if (!initial) return false;
    if (initial.length !== days.length) return true;
    const a = [...initial].sort();
    const b = [...days].sort();
    return a.join() !== b.join();
  }, [initial, days]);

  const toggle = (d: WeekDay) => {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  return (
    <div className="flex items-start justify-between gap-3 p-4">
      <div className="flex-1">
        <span className="block text-sm text-fg dark:text-fg-dark mb-2">
          {t("settings.weekend_days")}
        </span>
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map((d) => {
            const on = days.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggle(d)}
                className={`rounded-full px-3 py-1 text-xs ${
                  on
                    ? "bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark"
                    : "bg-hairline/40 dark:bg-hairline-dark/60 text-fg dark:text-fg-dark"
                }`}
              >
                {t(`weekday.${d}`)}
              </button>
            );
          })}
        </div>
      </div>
      <SaveButton
        onClick={async () => {
          await save(days);
          setSavedAt(Date.now());
        }}
        disabled={!dirty || busy}
        busy={busy}
        savedAt={savedAt}
      />
    </div>
  );
}

interface BoolRowProps {
  settingKey: SettingKey;
  initial: boolean | undefined;
  save: (value: boolean) => Promise<void>;
  busyKey: SettingKey | false;
}

/** Toggle switch row for the four sound boolean settings. Mirrors the
 *  other row components' "edit then explicit save" flow — a slip on a
 *  per-keyword toggle shouldn't update the server until the operator
 *  confirms. */
function BoolRow({ settingKey, initial, save, busyKey }: BoolRowProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<boolean | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (initial !== undefined) setValue(initial);
  }, [initial]);

  const dirty = value !== null && initial !== undefined && value !== initial;
  const busy = busyKey === settingKey;
  const checked = value ?? initial ?? false;

  return (
    <div className="flex items-center justify-between gap-3 p-4">
      <label className="flex-1 flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setValue(e.target.checked)}
          className="h-4 w-4 rounded border-hairline dark:border-hairline-dark"
        />
        <span className="text-sm text-fg dark:text-fg-dark">
          {t(`settings.${settingKey}`)}
        </span>
      </label>
      <SaveButton
        onClick={async () => {
          if (value === null) return;
          await save(value);
          setSavedAt(Date.now());
        }}
        disabled={!dirty || busy}
        busy={busy}
        savedAt={savedAt}
      />
    </div>
  );
}

interface VolumeRowProps {
  initial: number | undefined;
  save: (value: number) => Promise<void>;
  busy: boolean;
}

/** 0–100 range slider for `sound_volume`. The current value is shown
 *  alongside the slider so the operator can target an exact number
 *  without dropping into an input field. */
function VolumeRow({ initial, save, busy }: VolumeRowProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<number | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (initial !== undefined) setValue(initial);
  }, [initial]);

  const current = value ?? initial ?? 0;
  const dirty = value !== null && initial !== undefined && value !== initial;

  return (
    <div className="flex items-center justify-between gap-3 p-4">
      <label className="flex-1">
        <span className="block text-sm text-fg dark:text-fg-dark mb-1">
          {t("sound.volume")}: {current}
        </span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={current}
          onChange={(e) => setValue(Number(e.target.value))}
          className="w-full max-w-sm accent-pulse dark:accent-pulse-dark"
        />
      </label>
      <SaveButton
        onClick={async () => {
          if (value === null) return;
          await save(value);
          setSavedAt(Date.now());
        }}
        disabled={!dirty || busy}
        busy={busy}
        savedAt={savedAt}
      />
    </div>
  );
}

interface SaveButtonProps {
  onClick: () => Promise<void> | void;
  disabled: boolean;
  busy: boolean;
  savedAt: number | null;
}

function SaveButton({ onClick, disabled, busy, savedAt }: SaveButtonProps) {
  const { t } = useTranslation();
  // Show the "saved" check for 2 seconds after a successful save so
  // the user knows it landed; React Query's invalidate then resyncs.
  const showSaved = savedAt !== null && Date.now() - savedAt < 2000;
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={disabled}
      className="inline-flex items-center gap-1 rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold disabled:opacity-50 self-end"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : showSaved ? (
        <Check className="h-4 w-4" aria-hidden />
      ) : null}
      {showSaved ? t("settings.saved") : t("common.save")}
    </button>
  );
}
